import type { FixablePosition } from "v-network-graph";
import { markRaw } from "vue";
import {
  AccessTokenType,
  ExternalHostType,
  FlowType,
  HostType,
  HostGroupType,
  HostGroupMetaType,
  TreeNode,
  ExternalHostMetaType,
  Edge,
  BlacklistItemType,
  FlowFilter,
  HostInterfaceType,
  ConversationItem,
  NodeStatusLabel,
  UUIDClass,
  RecommendationStatusType,
  RecommendationStatus,
  RecommendationFlow,
  TabType,
  UserRole,
  GenericRowType,
  LayoutInfo,
  emptyLayoutInfo,
} from "./types";
import { TreeNodes } from "./types";
import { storage } from "@/storage/layout";
import { toCount } from "@/utils/filterRange";
import style from "@/style.json";
import CircleIcon from "@/icons/circle.svg";
import GroupNodeIcon from "@/icons/group-node.svg";
import TagTabIcon from "@/icons/tag-tab.svg";
import WorldIcon from "@/icons/world.svg";
import EventsIcon from "@/icons/events.svg";
import AlertIcon from "@/icons/alert.svg";
import LockIcon from "@/icons/lock.svg";
import RoleIcon from "@/icons/role.svg";
import OverviewIcon from "@/icons/overview.svg";
import MailIcon from "@/icons/mail.svg";
import NetShieldIcon from "@/icons/net-shield.svg";
import InfoCircleIcon from "@/icons/info-circle.svg";
import SettingsIcon from "@/icons/settings.svg";
import { notify as notifyToast } from "@kyvg/vue3-notification";
import { isAbortError } from "@/utils/abort";

export function errorText(err: unknown, fallback = "Unknown error"): string {
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message?: unknown }).message ?? fallback);
  }
  try {
    return String(err);
  } catch {
    return fallback;
  }
}

export function isUnauthenticatedError(err: unknown): boolean {
  const text = errorText(err, "").toLowerCase();
  return (
    text === "no access token provided" ||
    text === "unauthorized" ||
    text.includes("unable to authenticate") ||
    text.includes("token is not valid")
  );
}

export function notifyFromCatch(
  err: unknown,
  fallback: string,
  type: "error" | "warn" = "error",
) {
  if (isAbortError(err) || isUnauthenticatedError(err)) return;
  notifyToast({ type, text: errorText(err, fallback) });
}

export function setCookie(name: string, value: string, days: number) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "; expires=" + date.toUTCString();
  document.cookie =
    name + "=" + (value || "") + expires + "; path=/;SameSite=Lax";
}

export function getCookie(name: string) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

const HAS_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})$/;

export function dateToUtcStartOfDay(dateStr: string): string {
  const match = dateStr.trim().match(HAS_TIME_PATTERN);
  if (match) {
    const d = new Date(
      parseInt(match[1].slice(0, 4)),
      parseInt(match[1].slice(5, 7)) - 1,
      parseInt(match[1].slice(8, 10)),
      parseInt(match[2].slice(0, 2)),
      parseInt(match[2].slice(3, 5)),
      parseInt(match[2].slice(6, 8)),
    );
    if (isNaN(d.getTime())) return dateStr;
    return d.toISOString();
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function dateToUtcEndOfDay(dateStr: string): string {
  const match = dateStr.trim().match(HAS_TIME_PATTERN);
  if (match) {
    const d = new Date(
      parseInt(match[1].slice(0, 4)),
      parseInt(match[1].slice(5, 7)) - 1,
      parseInt(match[1].slice(8, 10)),
      parseInt(match[2].slice(0, 2)),
      parseInt(match[2].slice(3, 5)),
      parseInt(match[2].slice(6, 8)),
    );
    if (isNaN(d.getTime())) return dateStr;
    return d.toISOString();
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

const USER_LOGIN_STORAGE_KEY = "user_login";

export function setUserLogin(login: string) {
  localStorage.setItem(USER_LOGIN_STORAGE_KEY, login);
}

export function getUserLogin(): string {
  return localStorage.getItem(USER_LOGIN_STORAGE_KEY) ?? "";
}

export function clearUserLogin() {
  localStorage.removeItem(USER_LOGIN_STORAGE_KEY);
}

function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload);
}

export function parseAccessToken(): AccessTokenType | null {
  const accessTokenCookie = getCookie("access_token");
  if (!accessTokenCookie) return null;
  try {
    return parseJwt(accessTokenCookie);
  } catch {
    return null;
  }
}

/** JWT is past its `exp` (seconds since epoch). */
export function isTokenExpired(token: AccessTokenType): boolean {
  return token.exp * 1000 < Date.now();
}

/** Token is present and not expired — the real "is authenticated" check. */
export function isTokenValid(
  token: AccessTokenType | null,
): token is AccessTokenType {
  return token != null && !isTokenExpired(token);
}

export function getUserRoles(): string[] {
  const parsedJWT = parseAccessToken();
  if (parsedJWT == null) return [];
  return parsedJWT.roles;
}

type checkBlacklist = {
  interfaces?: HostInterfaceType[];
  class?: UUIDClass;
  id: string;
};

export function inBlacklist(
  blacklist: BlacklistItemType[],
  host: checkBlacklist,
): boolean {
  return blacklist.some(
    (b) =>
      b.item === host.id || host.interfaces?.some((iface) => iface.id === b.item),
  );
}

export function colorByNode(n: TreeNode): string {
  if (n.blacklisted) return "#ff5555";
  switch (n.class) {
    case "UUID_CLASS_EXTERNAL":
      return style.colors.blue.primary;
    case "UUID_CLASS_AGENT":
      if (n.dead_dttm && n.dead_dttm !== "unknown") {
        return style.colors.gray.primary_opaced;
      }
      if (n.is_unknown) {
        return style.colors.gray.primary_opaced;
      } else if (n.mode === "AGENT_MODE_DISCOVERY" && n.rulesCount === 0) {
        return style.colors.yellow.primary;
      } else if (n.mode === "AGENT_MODE_ENFORCING" || n.rulesCount! > 0) {
        return "#8F959E";
      }
      return "#8F959E";
    case "UUID_CLASS_GROUP":
      return style.colors.white.primary;
    default:
      return "#8888AA";
  }
}

export function getPorts(filterString?: string): number[][] {
  if (filterString === undefined) return [];
  const filter: FlowFilter = JSON.parse(filterString);
  return filter.ports ?? [];
}

export function parseSemicolonSeparatedIds(v: string | undefined): string[] {
  if (v == null || !String(v).trim()) return [];
  return String(v)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function collectFlowEndpointIds(
  ...values: (string | undefined)[]
): string[] {
  return Array.from(
    new Set(
      values.flatMap((v) =>
        typeof v === "string" && v !== "{}" && v.trim() !== ""
          ? parseSemicolonSeparatedIds(v)
          : [],
      ),
    ),
  );
}

export function portInFilter(port: number, filterPorts: number[][]): boolean {
  return filterPorts.some((f) =>
    f.length == 0
      ? false
      : f.length == 1
        ? f[0] == port
        : port >= f[0] && port <= f[0] + f[1],
  );
}

/**
 * Максимум слотов-интерфейсов, раскладываемых по кольцу вокруг узла.
 * Интерфейсы сверх капа не получают слот (их рёбра ведутся в центр узла).
 */
export const MAX_INTERFACE_SLOTS = 12;

export function interfaceSlotCount(nInterfaces: number): number {
  return Math.max(0, Math.min(nInterfaces, MAX_INTERFACE_SLOTS));
}

function ringAngle(i: number, n: number): number {
  return -Math.PI / 2 + (i * 2 * Math.PI) / n;
}

function ringRadiusScale(n: number): number {
  return n <= 6 ? 1 : 1.35;
}

/**
 * Базовый множитель радиуса кольца интерфейсов (в единицах `size`). Отодвигает
 * кружки и подписи IP от узла настолько, чтобы верхняя подпись не наезжала на хост.
 * Единый источник радиуса для скелетона, усов и якорей рёбер.
 */
const INTERFACE_RING_FACTOR = 5;

function interfaceRingRadius(size: number, n: number): number {
  return size * INTERFACE_RING_FACTOR * ringRadiusScale(n);
}

/** Радиус кружка интерфейса в координатах графа (якорь ребра упирается в его край). */
export const INTERFACE_CIRCLE_RADIUS = 15;

/** Радиальный вынос подписи IP от центра кружка. */
const INTERFACE_LABEL_OUTSET = 20;

/** |cos|/|sin| ниже этого — слот считаем «почти вертикальным/горизонтальным». */
const INTERFACE_LABEL_AXIS = 0.4;

export type InterfaceLabelAnchor = "start" | "middle" | "end";
export type InterfaceLabelBaseline = "auto" | "middle" | "hanging";

/**
 * Смещение и якоря подписи IP относительно центра кружка.
 * Учитывает SVG baseline: снизу `hanging` (глифы вниз), сверху `auto` (вверх),
 * по бокам `middle` + start/end, чтобы текст не наезжал на кружок.
 */
export function interfaceLabelLayout(
  cos: number,
  sin: number,
  outset = INTERFACE_LABEL_OUTSET,
): {
  x: number;
  y: number;
  textAnchor: InterfaceLabelAnchor;
  dominantBaseline: InterfaceLabelBaseline;
} {
  if (Math.abs(cos) < INTERFACE_LABEL_AXIS) {
    return {
      x: 0,
      y: sin < 0 ? -outset : outset,
      textAnchor: "middle",
      dominantBaseline: sin < 0 ? "auto" : "hanging",
    };
  }
  if (Math.abs(sin) < INTERFACE_LABEL_AXIS) {
    return {
      x: (cos < 0 ? -1 : 1) * outset,
      y: 0,
      textAnchor: cos < 0 ? "end" : "start",
      dominantBaseline: "middle",
    };
  }
  return {
    x: outset * cos,
    y: outset * sin,
    textAnchor: cos < 0 ? "end" : "start",
    dominantBaseline: sin < 0 ? "auto" : "hanging",
  };
}

/**
 * Радиус hit-area ореола интерфейсов вокруг узла: кольцо + кружок + вынос IP.
 * Совпадает с визуалом covid при `size = 15`.
 */
export function interfaceHaloRadius(nInterfaces: number, size = 15): number {
  const n = interfaceSlotCount(nInterfaces);
  if (n <= 0) return 0;
  return (
    interfaceRingRadius(size, n) +
    INTERFACE_CIRCLE_RADIUS +
    INTERFACE_LABEL_OUTSET
  );
}

/**
 * Какой интерфейс под точкой графа (ближайший слот в радиусе кружка), либо undefined.
 */
export function hitTestHostInterface(
  interfaces: { id: string }[] | undefined,
  nodePos: { x: number; y: number },
  point: { x: number; y: number },
  size = 15,
): string | undefined {
  if (!interfaces?.length) return undefined;
  const offsets = interfaceChartOffsets(interfaces.length, size);
  let bestId: string | undefined;
  let bestDist = INTERFACE_CIRCLE_RADIUS;
  for (let i = 0; i < offsets.length; i++) {
    const id = interfaces[i]?.id;
    if (!id) continue;
    const dx = nodePos.x + offsets[i].circle.x - point.x;
    const dy = nodePos.y + offsets[i].circle.y - point.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= bestDist) {
      bestDist = dist;
      bestId = id;
    }
  }
  return bestId;
}

/**
 * Позиции кружков-интерфейсов и подписей IP, равномерно по кольцу вокруг узла.
 * Единый источник геометрии и для covid-скелетона, и для якорей рёбер.
 * @param nInterfaces число интерфейсов узла (обрезается до MAX_INTERFACE_SLOTS)
 * @param size радиус-«шаг» (в скелетоне = 15)
 */
export function interfaceChartOffsets(nInterfaces: number, size = 15) {
  const n = interfaceSlotCount(nInterfaces);
  const R = interfaceRingRadius(size, n);
  const res: {
    circle: { x: number; y: number };
    text: {
      x: number;
      y: number;
      textAnchor: InterfaceLabelAnchor;
      dominantBaseline: InterfaceLabelBaseline;
    };
  }[] = [];
  for (let i = 0; i < n; i++) {
    const a = ringAngle(i, n);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    res.push({
      circle: { x: R * cos, y: R * sin },
      text: interfaceLabelLayout(cos, sin),
    });
  }
  return res;
}

export function getInterfaceLines(
  node: FixablePosition,
  size: number,
  nodeSize: number,
  nInterfaces: number,
) {
  const n = interfaceSlotCount(nInterfaces);
  const interfaceRadius = (nodeSize * size) / 40;
  const innerR = size * 1.4;
  const outerR = interfaceRingRadius(size, n) - interfaceRadius;
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  for (let i = 0; i < n; i++) {
    const a = ringAngle(i, n);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    lines.push({
      x1: node.x + cos * outerR,
      y1: node.y + sin * outerR,
      x2: node.x + cos * innerR,
      y2: node.y + sin * innerR,
    });
  }
  return lines;
}

export function interfaceRingArcs(
  nInterfaces: number,
  radius: number,
  gapDeg = 20,
): string[] {
  const n = interfaceSlotCount(nInterfaces);
  if (n <= 0) return [];
  const spacing = (2 * Math.PI) / n;
  // Промежуток не больше 35% шага между интерфейсами — иначе при плотном кольце
  // (n≈10–12) фиксированный gap «съел» бы дугу целиком.
  const gap = Math.min((gapDeg * Math.PI) / 180, spacing * 0.35);
  const r2 = (v: number) => Math.round(v * 100) / 100;
  const arcs: string[] = [];
  for (let i = 0; i < n; i++) {
    const a0 = ringAngle(i, n) + gap; // сразу после интерфейса i
    const a1 = ringAngle(i + 1, n) - gap; // перед интерфейсом i+1 (для i=n-1 — с обходом)
    if (a1 - a0 <= 0.001) continue; // промежуток «съел» всю дугу
    const x0 = radius * Math.cos(a0);
    const y0 = radius * Math.sin(a0);
    const x1 = radius * Math.cos(a1);
    const y1 = radius * Math.sin(a1);
    const largeArc = a1 - a0 > Math.PI ? 1 : 0;
    arcs.push(
      `M ${r2(x0)} ${r2(y0)} A ${r2(radius)} ${r2(radius)} 0 ${largeArc} 1 ${r2(x1)} ${r2(y1)}`,
    );
  }
  return arcs;
}

/**
 * Смещение якоря интерфейса от центра узла (в тех же координатах, что рисует кружок),
 * либо null, если у интерфейса нет слота (не найден или сверх MAX_INTERFACE_SLOTS) —
 * тогда ребро ведётся в центр узла. Единый источник с `interfaceChartOffsets`.
 */
export function interfaceAnchorOffset(
  interfaces: { id: string }[] | undefined,
  ifaceId: string | null | undefined,
  size = 15,
): { x: number; y: number } | null {
  if (!interfaces || !ifaceId) return null;
  const slot = interfaces.findIndex((i) => i.id === ifaceId);
  if (slot < 0 || slot >= MAX_INTERFACE_SLOTS) return null;
  return interfaceChartOffsets(interfaces.length, size)[slot]?.circle ?? null;
}

const IPV4_ADDRESS_RE =
  /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

/** IPv4 dotted-quad or CIDR (e.g. 10.0.0.0/24). Empty string is invalid here. */
export function isIPv4OrCidr(value: string): boolean {
  const s = value.trim();
  if (!s) return false;
  if (!s.includes("/")) {
    return IPV4_ADDRESS_RE.test(s);
  }
  const parts = s.split("/");
  if (parts.length !== 2 || parts[0] === "" || parts[1] === "") {
    return false;
  }
  if (!IPV4_ADDRESS_RE.test(parts[0])) {
    return false;
  }
  const prefix = Number(parts[1]);
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) {
    return false;
  }
  if (parts[1] !== String(prefix)) {
    return false;
  }
  return true;
}

export function isIP(ip: string) {
  const justIp = ip.split("/")[0];
  return IPV4_ADDRESS_RE.test(justIp);
}

export function copy<T extends Record<string, any>>(
  obj: T,
  sorted?: boolean,
): T {
  const stringify = sorted ? stringifySorted<T> : JSON.stringify;
  return JSON.parse(stringify(obj));
}

function hostToTreeNode(
  host: HostType,
  flowsByIface: Map<string, number>,
  ifaceToGroupIds: Map<string, string[]>,
): TreeNode {
  let totalRulesCount = 0;

  const seenGroups = new Set<string>();
  host.interfaces.forEach((iface) => {
    totalRulesCount += flowsByIface.get(iface.id) ?? 0;
    for (const groupId of ifaceToGroupIds.get(iface.id) ?? []) {
      if (seenGroups.has(groupId)) continue;
      seenGroups.add(groupId);
      totalRulesCount += flowsByIface.get(groupId) ?? 0;
    }
  });

  return {
    is_unknown: host?.is_unknown,
    id: host.agent_id,
    name: host.hostname,
    interfaces: host.interfaces,
    class: "UUID_CLASS_AGENT",
    blacklisted: false,
    status_label: undefined,
    dead_dttm: host.dead_dttm,
    mode: host.mode,
    rulesCount: totalRulesCount,
    kernel: host.info.kernel,
  };
}

export function generateTreeNodes(
  hosts: HostType[],
  groups: HostGroupType[],
  flows: FlowType[] = [],
  layoutInfo?: LayoutInfo,
): TreeNodes {
  // User meta often ships `layout_info: {}` (e2e mocks, old accounts). Spread
  // over defaults so `group_unwraps[id]` below cannot throw and empty the tree.
  const resolvedLayout: LayoutInfo = {
    ...emptyLayoutInfo(),
    ...(layoutInfo ?? storage.getLayoutInfo()),
  };
  const nodeTree: TreeNodes = {};

  // Индекс iface → host для O(1) поиска вместо O(hosts × interfaces)
  const ifaceToHost = new Map<string, HostType>();
  const hostById = new Map<string, HostType>();
  hosts.forEach((host) => {
    hostById.set(host.agent_id, host);
    host.interfaces.forEach((iface) => ifaceToHost.set(iface.id, host));
  });

  // Индекс iface → кол-во правил для O(1) подсчёта вместо flows.filter()
  const flowsByIface = new Map<string, number>();
  flows.forEach((flow) => {
    flowsByIface.set(flow.src, (flowsByIface.get(flow.src) ?? 0) + 1);
    flowsByIface.set(flow.dst, (flowsByIface.get(flow.dst) ?? 0) + 1);
  });

  const ifaceToGroupIds = new Map<string, string[]>();
  groups.forEach((group) => {
    if (!group.id) return;
    group.interface_uuids.forEach((ifaceId) => {
      const list = ifaceToGroupIds.get(ifaceId);
      if (list) list.push(group.id!);
      else ifaceToGroupIds.set(ifaceId, [group.id!]);
    });
  });

  const unusedHostsSet = new Set(hosts.map((host) => host.agent_id));

  const hostNodeCache = new Map<string, TreeNode>();
  const hostNodeFor = (host: HostType): TreeNode => {
    let n = hostNodeCache.get(host.agent_id);
    if (!n) {
      n = hostToTreeNode(host, flowsByIface, ifaceToGroupIds);
      hostNodeCache.set(host.agent_id, n);
    }
    return n;
  };

  groups.forEach((group) => {
    const meta: HostGroupMetaType = JSON.parse(group.meta);
    const children: TreeNodes = {};

    // Итерируем только интерфейсы группы вместо всех хостов × интерфейсов
    group.interface_uuids.forEach((ifaceId) => {
      const host = ifaceToHost.get(ifaceId);
      if (!host) return;
      children[ifaceId] = hostNodeFor(host);
      unusedHostsSet.delete(host.agent_id);
    });

    const childrenIds = Object.keys(children);
    const childrenIdSet = new Set(childrenIds);

    const oldUnwrappedChildren = resolvedLayout.group_unwraps[group.id!] ?? [];

    const validUnwrapped = oldUnwrappedChildren.filter((id: string) =>
      childrenIdSet.has(id),
    );
    const useOldUnwrapped = validUnwrapped.length > 0;

    // markRaw prevents Vue from deeply reactifying 11K+ TreeNode objects inside
    // children when the parent group is assigned to a reactive nodeTree.
    // Children are only accessed imperatively (nodeById, walkAllNodes) — never
    // from templates/computed — so they don't need reactivity. Without this,
    // assigning pseudo_group (5000+ children) triggers creation of ~75K+ Proxy
    // objects and blocks the main thread for seconds.
    nodeTree[group.id!] = {
      id: group.id!,
      name: meta.name,
      class: "UUID_CLASS_GROUP",
      children: markRaw(children),
      unwrapped: useOldUnwrapped ? validUnwrapped : [],
      blacklisted: false,
    };
  });

  unusedHostsSet.forEach((id) => {
    const host = hostById.get(id)!;
    nodeTree[id] = hostNodeFor(host);
  });

  return nodeTree;
}

export function externalHostToTreeNode(host: ExternalHostType): TreeNode {
  let name =
    host.dns.length > 0
      ? host.dns[0]
      : host.cidrs.length == 1
        ? host.cidrs[0]
        : host.id!;
  if (host.meta) {
    const meta: ExternalHostMetaType = JSON.parse(host.meta);
    name = meta.name;
  }
  return {
    id: host.id!,
    name: name,
    class: "UUID_CLASS_EXTERNAL",
    blacklisted: false,
  };
}

type NodeLookupResult = {
  id: string | null;
  ifaceId: string | null;
  type: string | null;
};

function findNodeId(
  flowId: string,
  ifaceToHost: Map<string, HostType>,
  extHostById: Map<string, ExternalHostType>,
  groupById: Map<string, HostGroupType>,
  extHostsByCidr: Map<string, ExternalHostType>,
): NodeLookupResult {
  const srcHost = ifaceToHost.get(flowId);
  if (srcHost) return { id: srcHost.agent_id, ifaceId: flowId, type: "host" };

  const srcExtHost = extHostById.get(flowId);
  if (srcExtHost)
    return { id: srcExtHost.id!, ifaceId: null, type: "external" };

  const srcGroup = groupById.get(flowId);
  if (srcGroup) return { id: srcGroup.id!, ifaceId: null, type: "group" };

  if (flowId.startsWith("new_ext:")) {
    const extIp = flowId.split("new_ext:")[1];
    const recommendedExt = extHostsByCidr.get(extIp);
    if (recommendedExt)
      return { id: recommendedExt.id!, ifaceId: null, type: "external" };
  }

  return { id: null, ifaceId: null, type: null };
}

export function flowToEdge(
  flow: FlowType,
  sourceIface: string,
  targetIface: string,
): Edge {
  return {
    id: flow.id,
    source: flow.src,
    source_iface: sourceIface,
    target: flow.dst,
    target_iface: targetIface,
    filter_dst: flow.filter_dst,
    filter_src: flow.filter_src,
    dashed: false,
    directional: true,
    color: style.colors.white.primary,
    type: "rule",
  };
}

export function flowsToEdges(
  flows: (FlowType & { is_recommended?: boolean; recommended_id?: string })[],
  hosts: HostType[],
  extHosts: ExternalHostType[],
  groups: HostGroupType[],
  options?: { skipGroupMembership?: boolean },
): Edge[] {
  // Строим индексы один раз вместо линейного поиска для каждого flow
  const ifaceToHost = new Map<string, HostType>();
  hosts.forEach((h) => h.interfaces.forEach((i) => ifaceToHost.set(i.id, h)));

  const extHostById = new Map<string, ExternalHostType>();
  extHosts.forEach((e) => extHostById.set(e.id!, e));

  const groupById = new Map<string, HostGroupType>();
  groups.forEach((g) => groupById.set(g.id!, g));

  // Индекс CIDR → external host для "new_ext:" случая
  const extHostsByCidr = new Map<string, ExternalHostType>();
  extHosts.forEach((e) =>
    e.cidrs.forEach((cidr) => extHostsByCidr.set(cidr, e)),
  );

  const edges: Edge[] = [];

  flows.forEach((flow) => {
    const srcObj = findNodeId(
      flow.src,
      ifaceToHost,
      extHostById,
      groupById,
      extHostsByCidr,
    );
    const dstObj = findNodeId(
      flow.dst,
      ifaceToHost,
      extHostById,
      groupById,
      extHostsByCidr,
    );
    if (!srcObj.id || !dstObj.id) return;

    edges.push({
      id: flow.is_recommended
        ? `${flow?.recommended_id}-recommended`
        : `${flow.id!}`,
      source: srcObj.id,
      source_iface: srcObj.ifaceId,
      target: dstObj.id,
      target_iface: dstObj.ifaceId,
      filter_dst: flow.filter_dst,
      filter_src: flow.filter_src,
      dashed: flow.is_recommended === true,
      directional: true,
      color: flow.is_recommended
        ? style.colors.yellow.secondary
        : style.colors.white.primary,
      type: flow.is_recommended ? "recommendation" : "rule",
      recommendation_type: flow.is_recommended
        ? `${srcObj.type}-${dstObj.type}`
        : null,
    });
  });

  // Generating group_membership edges for all 227+ groups × ~49 interfaces creates
  // ~11 000 edge objects that registerEdge() immediately discards (dedup) when the
  // edges are already registered. Skip this when the caller only needs flow edges
  // (e.g. applyRecommendationsData which passes only recommendation flows).
  if (!options?.skipGroupMembership) {
    groups.forEach((group) => {
      group.interface_uuids.forEach((ifaceId) => {
        const src = group.id!;
        const dstObj = findNodeId(
          ifaceId,
          ifaceToHost,
          extHostById,
          groupById,
          extHostsByCidr,
        );
        if (!dstObj.id) return;
        edges.push({
          id: `${src}-${dstObj.ifaceId}`,
          source: src,
          target: dstObj.id,
          target_iface: dstObj.ifaceId,
          filter_dst: "",
          filter_src: "",
          dashed: true,
          directional: false,
          color: style.colors.white.primary,
          type: "group_membership",
        });
      });
    });
  }

  return edges;
}

export function conversationToRule(conv: ConversationItem): FlowType {
  return {
    src: conv.src_agent,
    dst: conv.dst_agent,
    filter_dst: JSON.stringify({
      ports: conv.service_port_is_src ? [] : [[conv.service_port]],
      proto: [conv.proto],
    }),
    filter_src: JSON.stringify({
      ports: conv.service_port_is_src ? [[conv.service_port]] : [],
      proto: [conv.proto],
    }),
  };
}

export function formatBytes(bytesIn: string, fixed = 2) {
  const bytes = parseInt(bytesIn);
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(fixed))} ${sizes[i]}`;
}

export function protocolByNumber(num: number): string {
  if (num == 6) return "TCP";
  if (num == 17) return "UDP";
  if (num == 1) return "ICMP";
  if (num == 0) return "ANY";
  return num.toString();
}

export function protocolsByNumber(ports: number[]): string {
  if (ports.length === 0) return "[ANY]";
  const NumToPort: Record<number, string> = {
    6: "TCP",
    17: "UDP",
    1: "ICMP",
    0: "ANY",
  };

  return `[${ports.map((port) => NumToPort[port] || "UNKNOWN").join(", ")}]`;
}

export function getProtocolsInNumbersByFlow(flow: FlowType) {
  if (!flow.filter_dst) return [];
  try {
    const proto = (JSON.parse(flow.filter_dst) as FlowFilter).proto;
    return proto ?? [];
  } catch {
    return [];
  }
}

export type CheckPortsError =
  | "errors.empty_string"
  | "errors.invalid_literals"
  | "errors.nan"
  | "errors.invalid_range_length"
  | "errors.range_second_is_not_greater"
  | "errors.port_too_big"
  | "errors.port_is_negative"
  | "";

export function checkPorts(ports: string): CheckPortsError {
  if (ports.trim().length === 0) return "errors.empty_string";

  const allowedPattern = /^[0-9,-]+(,[0-9,-]+)*$/;
  if (!allowedPattern.test(ports)) return "errors.invalid_literals";

  const parts = ports.split(",");

  for (const part of parts) {
    const rangeSplit = part.split("-");

    if (rangeSplit.length > 2 || rangeSplit.length === 0) {
      return "errors.invalid_range_length";
    }

    const start = parseInt(rangeSplit[0], 10);
    if (isNaN(start)) return "errors.nan";

    if (rangeSplit.length === 2) {
      const end = parseInt(rangeSplit[1], 10);
      if (isNaN(end)) return "errors.nan";
      if (start >= end) return "errors.range_second_is_not_greater";
      if (start < 0 || end < 0) return "errors.port_is_negative";
      if (start > 65535 || end > 65535) return "errors.port_too_big";
    } else {
      if (start < 0) return "errors.port_is_negative";
      if (start > 65535) return "errors.port_too_big";
    }
  }

  return "";
}

export function parsePorts(ports: string): number[][] {
  if (ports.trim().length === 0) return [];

  const parts = ports.split(",").map((p) => p.trim());

  return parts.map((range) => {
    const rangeSplit = range.split("-");
    const start = parseInt(rangeSplit[0], 10);

    if (rangeSplit.length === 1) {
      return [start];
    }

    const end = parseInt(rangeSplit[1], 10);
    return [start, end - start];
  });
}

export function unparsePorts(
  ports: (number[] | null)[] | undefined | null,
  withDash: boolean = false,
): string {
  if (!ports || ports.length === 0) {
    return withDash ? "-" : "";
  }

  if (ports.length === 1 && ports[0]?.length == 0) return "";

  return ports
    .map((range) => {
      if (!Array.isArray(range)) return "";
      if (range.some((v) => v === null || typeof v !== "number")) return "";

      return range.length === 1
        ? range[0].toString()
        : `${range[0]}-${range[0] + range[1]}`;
    })
    .filter(Boolean)
    .join(",");
}

export function unparsePortsList(
  ports: (number[] | null)[] | undefined | null,
): string[] {
  const value = unparsePorts(ports);
  if (!value) return [];
  return value.split(",");
}

export enum Protocol {
  ANY = 0,
  TCP = 6,
  UDP = 17,
  ICMP = 1,
}

export function protoToNumber(proto: string): number {
  switch (proto.toLowerCase()) {
    case "tcp":
      return Protocol.TCP;
    case "udp":
      return Protocol.UDP;
    case "icmp":
      return Protocol.ICMP;
    case "any":
      return Protocol.ANY;
    default:
      return -1;
  }
}

export function extractBracketValue(input: string): string {
  const match = input.match(/\[(.*?)]/);
  return match ? match[1] : input;
}

export function protoToName(proto: number | string): string {
  const p = parseInt(proto.toString());
  switch (p) {
    case 6:
      return "tcp";
    case 17:
      return "udp";
    case 1:
      return "icmp";
    case 0:
      return "any";
    default:
      return p.toString();
  }
}

export function parseProto(filterString: string): string {
  if (filterString.length == 0) return "";
  const json = JSON.parse(filterString);
  if (!("proto" in json)) return "";
  if (json.proto.length == 0) return "";
  return protoToName(json.proto[0]);
}

export function jsonEquals<T>(t1: T, t2: T) {
  return JSON.stringify(t1) == JSON.stringify(t2);
}

export function statusTextColor(status: NodeStatusLabel | undefined): string {
  switch (status) {
    case "warning":
      return "#DBBB48";
    case "critical":
      return "#DB5948";
    default:
      return "#EDEDED";
  }
}

export function statusBgColor(status: NodeStatusLabel | undefined): string {
  switch (status) {
    case "warning":
      return "#00000033";
    case "critical":
      return "#3d1f28";
    default:
      return "#00000033";
  }
}

export function tagBorderColor(tagColor: string): string {
  switch (tagColor) {
    case "red":
      return "border-red-primary text-red-primary";
    case "yellow":
      return "border-yellow-primary text-yellow-primary";
    case "blue":
      return "border-blue-primary text-blue-primary";
    case "green":
      return "border-green-primary text-green-primary";
    case "white":
      return "border-white-primary text-white-primary";
    default:
      return "border-white-primary";
  }
}

export function tagBlockColor(tagColor: string): string {
  switch (tagColor) {
    case "red":
      return "bg-red-secondary text-white-primary";
    case "yellow":
      return "bg-yellow-secondary text-white-primary";
    case "blue":
      return "bg-blue-secondary text-white-primary";
    case "green":
      return "bg-green-secondary text-white-primary";
    case "white":
      return "bg-white-primary text-black-primary";
    case "orange":
      return "bg-orange-secondary text-white-primary";
    case "violet":
      return "bg-violet-secondary text-white-primary";
    case "indigo":
      return "bg-indigo-secondary text-white-primary";
    default:
      return "bg-transparent";
  }
}

export function tagCircleColor(tagColor: string): string {
  switch (tagColor) {
    case "red":
      return "bg-red-primary text-white-primary";
    case "yellow":
      return "bg-yellow-primary text-white-primary";
    case "blue":
      return "bg-blue-primary text-white-primary";
    case "green":
      return "bg-green-primary text-white-primary";
    case "white":
      return "bg-white-primary text-black-primary";
    case "orange":
      return "bg-orange-primary text-white-primary";
    case "violet":
      return "bg-violet-primary text-white-primary";
    case "indigo":
      return "bg-indigo-primary text-white-primary";
    default:
      return "bg-transparent";
  }
}

export function flowsEqual(f1: FlowType, f2: FlowType) {
  return (
    f1.src == f2.src &&
    f1.dst == f2.dst &&
    (f1.filter_src === f2.filter_src ||
      (f1.filter_src !== undefined &&
        f2.filter_src !== undefined &&
        stringifySorted(JSON.parse(f1.filter_src)) ==
          stringifySorted(JSON.parse(f2.filter_src)))) &&
    stringifySorted(JSON.parse(f1.filter_dst)) ==
      stringifySorted(JSON.parse(f2.filter_dst))
  );
}

export function stringifySorted<T extends Record<string, any>>(obj: T): string {
  const ordered: Partial<T> = Object.keys(obj)
    .sort()
    .reduce((acc, key) => {
      acc[key as keyof T] = obj[key as keyof T];
      return acc;
    }, {} as Partial<T>);
  return JSON.stringify(ordered);
}

export function findHost(hosts: HostType[], id: string) {
  return hosts.find(
    (h) => h.agent_id == id || h.interfaces.some((i) => i.id == id),
  );
}

function ipToBinary(ip: string) {
  return ip
    .split(".")
    .map((octet) => parseInt(octet, 10).toString(2).padStart(8, "0"))
    .join("");
}

function getNetworkAddress(cidr: string) {
  const [ip, prefixLength] = cidr.split("/");
  const binaryIp = ipToBinary(ip);

  const networkBinary = binaryIp
    .substring(0, parseInt(prefixLength))
    .padEnd(32, "0");

  const networkAddress = [];
  for (let i = 0; i < 32; i += 8) {
    const octet = networkBinary.substring(i, i + 8);
    networkAddress.push(parseInt(octet, 2));
  }

  return networkAddress.join(".") + "/" + prefixLength;
}

export function groupBySubnets(hosts: HostType[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};

  hosts.forEach((h) =>
    h.interfaces.forEach((i) => {
      const networkAddress = getNetworkAddress(i.ip);

      if (!groups[networkAddress]) groups[networkAddress] = [];

      groups[networkAddress].push(h.agent_id);
    }),
  );

  return groups;
}

export function getGroupsBySubnetsFromIfaces(
  interfaces: HostInterfaceType[],
  onlyInterfaceIds?: Set<string>,
): Record<string, string[]> {
  const groups: Record<string, string[]> = {};

  interfaces.forEach((i) => {
    if (onlyInterfaceIds && !onlyInterfaceIds.has(String(i.id))) return;
    const networkAddress = getNetworkAddress(i.ip);

    if (!groups[networkAddress]) groups[networkAddress] = [];

    if (i.agent) groups[networkAddress].push(i.agent);
  });

  return groups;
}

export function getIfaceNames(ifaces: { name: any }[]) {
  return ifaces.map((i: { name: any }) => i.name).join("; ");
}

export function groupBySubnetsIfaces(
  hosts: HostType[],
): Record<string, string[]> {
  const groups: Record<string, string[]> = {};

  hosts.forEach((h) =>
    h.interfaces.forEach((i) => {
      const networkAddress = getNetworkAddress(i.ip);

      if (!groups[networkAddress]) groups[networkAddress] = [];

      groups[networkAddress].push(i.id);
    }),
  );

  return groups;
}

export function parseMetaName(
  meta: string | undefined,
  defaultValue: string,
): string {
  if (meta === undefined) return defaultValue;
  let name = defaultValue;
  try {
    name = JSON.parse(meta).name;
  } catch (err) {
    console.error("AgentMenu: bad meta", err);
  }
  return name;
}

export function recommendationStatusToBool(
  status: RecommendationStatusType,
): boolean | undefined {
  return status === RecommendationStatus.Accepted
    ? true
    : status == RecommendationStatus.Rejected
      ? false
      : undefined;
}

export function sortRecommendationsByConnectionsCount(
  items: RecommendationFlow[],
): RecommendationFlow[] {
  return [...items].sort(
    (a, b) => toCount(b.connections_count) - toCount(a.connections_count),
  );
}

export function isDeadDttmTooOld(date: string): boolean {
  const currentDate = new Date();
  const tooOld = new Date();
  tooOld.setDate(currentDate.getDate() - 7); // too old is 1 week #196

  return new Date(date) < tooOld;
}

export function deleteById<T extends { id?: string }>(arr: T[], id: string) {
  arr.splice(
    arr.findIndex((e) => e.id == id),
    1,
  );
}

export function generateHostsTabs(t: (arg: string) => string): TabType[] {
  const res: TabType[] = [
    {
      link: "/hosts/network",
      name: t("hosts.header_hosts"),
      icon: CircleIcon,
      iconClass: "text-green-primary",
    },
    {
      link: "/hosts/external",
      name: t("hosts.header_externals"),
      icon: CircleIcon,
      iconClass: "text-blue-primary",
    },
    {
      link: "/hosts/groups",
      name: t("hosts.header_groups"),
      icon: GroupNodeIcon,
      iconClass: "text-white-primary",
    },
    {
      link: "/hosts/tags",
      name: t("hosts.header_tags"),
      icon: TagTabIcon,
      iconClass: "text-white-primary",
    },
  ];
  return res;
}

export function generateEventsTabs(
  t: (arg: string) => string,
  tokenRoles?: UserRole[],
): TabType[] {
  const tabs: TabType[] = [
    { link: "/events/process", name: t("nav.process"), icon: EventsIcon },
    { link: "/events/alerts", name: t("nav.alerts"), icon: AlertIcon },
    { link: "/events/firewall", name: t("nav.firewall"), icon: LockIcon },
  ];

  if (
    !tokenRoles?.includes(UserRole.SegAdmin) &&
    !tokenRoles?.includes(UserRole.SegViewer)
  ) {
    return [
      { link: "/events/netflow", name: t("nav.netflow"), icon: WorldIcon },
      ...tabs,
    ];
  } else {
    return tabs;
  }
}

export function generateUserSettingsTabs(
  t: (arg: string) => string,
): TabType[] {
  const res = [
    {
      link: "/user-settings/profile",
      name: t("settings.my_account"),
      icon: CircleIcon,
    },
  ];
  if (getUserRoles().some((r) => r == UserRole.Admin)) {
    res.unshift(
      {
        link: "/user-settings/users",
        name: t("settings.users"),
        icon: RoleIcon,
      },
      {
        link: "/user-settings/segments",
        name: t("settings.system_segments"),
        icon: OverviewIcon,
      },
    );
  }
  return res;
}

export function generateGeneralSettingsTabs(
  t: (arg: string) => string,
): TabType[] {
  const res = [
    {
      link: "/general-settings/general",
      name: t("settings.general"),
      icon: SettingsIcon,
    },
  ];
  if (getUserRoles().some((r) => r == UserRole.Admin)) {
    res.push({
      link: "/general-settings/license",
      name: t("settings.license"),
      icon: InfoCircleIcon,
    });
  }
  return res;
}

export function generateNotificationsTabs(
  t: (arg: string) => string,
): TabType[] {
  return [
    {
      link: "/notifications/channels",
      name: t("notifications.transports"),
      icon: MailIcon,
    },
    {
      link: "/notifications/rules",
      name: t("notifications.notification_rules"),
      icon: NetShieldIcon,
    },
  ];
}
export function uriEncode(
  query: Record<string, string> | undefined,
  row: GenericRowType,
): string {
  return query
    ? "?" +
        Object.keys(query)
          .map((v) => `${v}=${row[v]}`)
          .join("&")
    : "";
}

type BrowserType = "chrome" | "firefox" | "safari" | "edge" | "other";

let browserCached: BrowserType | undefined;
export function checkBrowser(): BrowserType {
  if (browserCached === undefined) {
    const isChrome = navigator.userAgent.indexOf("Chrome") > -1;
    const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
    const isSafari = navigator.userAgent.indexOf("Safari") > -1;
    const isEdge = navigator.userAgent.indexOf("Edge") > -1;
    browserCached = isChrome
      ? "chrome"
      : isFirefox
        ? "firefox"
        : isSafari
          ? "safari"
          : isEdge
            ? "edge"
            : "other";
  }
  return browserCached;
}

export function ipToInt(ip: string): number {
  return ip.split(".").reduce((acc, octet) => (acc << 8) + parseInt(octet), 0);
}

export function isIpInSubnet(ip: string, subnet: string): boolean {
  const [subnetIp, prefix] = subnet.split("/");
  const subnetInt = ipToInt(subnetIp);
  const ipInt = ipToInt(ip);
  const mask = ~(2 ** (32 - parseInt(prefix)) - 1);
  return (ipInt & mask) === (subnetInt & mask);
}

export function capitalizeFirstLetter(str: string) {
  if (str === null || str === undefined || str.length === 0) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay = 300,
): (...args: Parameters<T>) => void {
  let timer: number | undefined;

  return (...args: Parameters<T>) => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), delay);
  };
}

export function checkValidPassword(password: string): [boolean, string[]] {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("errors.password_must_contain_at_least_8_characters");
  }
  if (!/\d/.test(password)) {
    errors.push("errors.password_must_contain_at_least_one_digit");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("errors.password_must_contain_at_least_one_special_character");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("errors.password_must_contain_at_least_one_uppercase_letter");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("errors.password_must_contain_at_least_one_lowercase_letter");
  }

  return [errors.length === 0, errors];
}
